import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormGroup,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { ReactComponent as LogoSVG } from "resources/logos/Aturna_Brandmark_RGB_Purple.svg";
import { ReactComponent as Logo2SVG } from "static/logos/logo-main.svg";
import styles from "screens/resetPassword/components/styles.module.scss";
import validatePasswordStrength from "utils/password";
import { API, post } from "modules/api";
import authProvider from "modules/auth/authProvider";

const PublicMedicaidVerificationPage: React.FC = () => {
  const { search } = useLocation();
  const token = new URLSearchParams(search).get("token") ?? "";

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showResendLink, setShowResendLink] = useState(false);
  const [passwordInstructionsVisible, setPasswordInstructionsVisible] =
    useState(false);
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resendState, setResendState] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");

  const NO_ERROR_MESSAGE = "";
  const PLEASE_TRY_AGAIN =
    "There was an error processing your request. Please try again.";

  const isErrorState = useCallback(() => errorMessage !== "", [errorMessage]);

  const handlePasswordChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const val = e.target.value;
    const isValid = validatePasswordStrength(val);
    setPasswordError(!isValid);
    setPasswordInstructionsVisible(!isValid);
    setPassword(val);
    setErrorMessage(NO_ERROR_MESSAGE);
  };

  const handleConfirmPasswordChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const val = e.target.value;
    setConfirmPassword(val);
    setConfirmPasswordError(val !== password);
    setErrorMessage(NO_ERROR_MESSAGE);
  };

  useEffect(() => {
    setSubmitButtonEnabled(
      password.length !== 0 &&
        confirmPassword.length !== 0 &&
        !passwordError &&
        confirmPassword === password,
    );
  }, [password, confirmPassword, passwordError]);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setResendState("idle");
    if (!token) {
      setErrorMessage(PLEASE_TRY_AGAIN);
      setShowResendLink(false);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await post<{
        medicaidLeadId: string;
        userId: string;
        email: string;
        partnerId: string | null;
      }>(API.PUBLIC_MEDICAID_VERIFICATION_COMPLETE(), { token, password });
      await authProvider.signInWithEmailAndPassword(data.email, password);

      setTimeout(() => {
        window.location.href = data.partnerId
          ? `/${data.partnerId.toLowerCase()}/medicaid/analysis/${data.medicaidLeadId}`
          : `/medicaid/analysis/${data.medicaidLeadId}`;
      }, 2000);
    } catch (err: any) {
      setSubmitting(false);
      const code = err?.response?.data?.short ?? "";
      const msg = err?.response?.data?.detail ?? PLEASE_TRY_AGAIN;

      if (code === "public-lead-verification-token-expired") {
        setErrorMessage(msg);
        setShowResendLink(true);
      } else if (code === "public-lead-verification-token-invalid") {
        setErrorMessage(msg);
        setShowResendLink(false);
      } else {
        setErrorMessage(PLEASE_TRY_AGAIN);
        setShowResendLink(false);
      }
    }
  };

  const handleResend = async () => {
    setResendState("sending");
    try {
      await post(API.PUBLIC_MEDICAID_VERIFICATION_RESEND(), { token });
      setResendState("sent");
      setErrorMessage(NO_ERROR_MESSAGE);
      setShowResendLink(false);
    } catch {
      setResendState("error");
    }
  };

  return (
    <Box component="div" className={styles.login}>
      <Box component="div" className={styles.login__image}>
        <LogoSVG aria-label="Logo" className={styles["login__image__logo-1"]} />
        <h2>Create Your Password</h2>
        <h3>Set a password to activate your account.</h3>
        <Logo2SVG
          aria-label="Logo"
          className={styles["login__image__logo-2"]}
        />
        <form>
          <FormGroup className={styles.login__form}>
            <FormControl
              className={styles.login__input_container}
              variant="outlined"
            >
              <InputLabel htmlFor="ui-verify-password-input">
                Password
              </InputLabel>
              <OutlinedInput
                className={styles.login__input_input}
                id="ui-verify-password-input"
                type={showPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
                onChange={handlePasswordChange}
                error={passwordError}
              />
            </FormControl>
            <FormControl
              className={styles.login__input_container}
              variant="outlined"
            >
              <InputLabel htmlFor="ui-verify-confirm-password-input">
                Confirm Password
              </InputLabel>
              <OutlinedInput
                className={styles.login__input_input}
                id="ui-verify-confirm-password-input"
                type={showConfirmPassword ? "text" : "password"}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Confirm Password"
                onChange={handleConfirmPasswordChange}
                placeholder="Re-enter password"
                error={confirmPasswordError}
              />
            </FormControl>
            <Box
              component="div"
              className={styles.login__error_message_container}
              sx={{ display: isErrorState() ? "block" : "none" }}
            >
              <Typography className={styles.login__error_message}>
                {errorMessage}
              </Typography>
              {showResendLink && (
                <Typography className={styles.login__error_message}>
                  {resendState === "error" ? (
                    "Failed to send a new link. Please contact your advisor."
                  ) : (
                    <Link onClick={handleResend}>
                      {resendState === "sending"
                        ? "Sending..."
                        : "Click here to request a new verification link."}
                    </Link>
                  )}
                </Typography>
              )}
            </Box>
            {resendState === "sent" && (
              <Box
                component="div"
                className={styles.login__error_message_container}
                sx={{ display: "block" }}
              >
                <Typography sx={{ color: "green", fontSize: "0.875rem" }}>
                  A new verification link has been sent to your email.
                </Typography>
              </Box>
            )}
            <Box
              component="div"
              className={styles.login__password_instructions_div}
              id="ui-verify-password-instructions"
              sx={{ display: passwordInstructionsVisible ? "block" : "none" }}
            >
              <Typography className={styles.login__password_instructions}>
                Your password should:
              </Typography>
              <Typography className={styles.login__password_requirements}>
                • be at least 8 characters long
              </Typography>
              <Typography className={styles.login__password_requirements}>
                • contain at least one uppercase and lowercase letter
              </Typography>
              <Typography className={styles.login__password_requirements}>
                • contain at least one number
              </Typography>
              <Typography className={styles.login__password_requirements}>
                • contain one of the following symbols: !@#$&*
              </Typography>
            </Box>
            <Box
              component="div"
              className={styles.login__input_button_container}
            >
              <Button
                variant="contained"
                className={styles.login__submit_button}
                type="submit"
                disabled={!submitButtonEnabled || submitting}
                onClick={handleSubmit}
              >
                Create Account
              </Button>
            </Box>
          </FormGroup>
        </form>
      </Box>
    </Box>
  );
};

export default PublicMedicaidVerificationPage;
